import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  EventNote as TemplateIcon,
  Weekend as WeekendIcon,
  Celebration as HolidayIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useStoreManagement, getSelectedStores } from '../../context/StoreManagementContext';
import { useStoresQuery } from '../../hooks/queries';

interface StoreHoursTemplate {
  id: string;
  name: string;
  type: 'custom' | 'holiday' | 'weekend' | 'business';
  hours: {
    monday: { open: string; close: string };
    tuesday: { open: string; close: string };
    wednesday: { open: string; close: string };
    thursday: { open: string; close: string };
    friday: { open: string; close: string };
    saturday: { open: string; close: string };
    sunday: { open: string; close: string };
  };
  createdAt: Date;
  lastUsed?: Date;
}

interface StoreHoursTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'save' | 'apply' | 'manage';
  selectedStoreId?: string;
  onApplyTemplate?: (templateId: string) => void;
}

export const StoreHoursTemplateDialog: React.FC<StoreHoursTemplateDialogProps> = ({
  open,
  onClose,
  mode,
  selectedStoreId,
  onApplyTemplate,
}) => {
  const { data: stores = [] } = useStoresQuery();
  const { selectedStoreIds } = useStoreManagement();
  const selectedStores = useMemo(() => getSelectedStores(stores, selectedStoreIds), [stores, selectedStoreIds]);

  // Mock templates - in real app these would come from backend
  const [templates] = useState<StoreHoursTemplate[]>([
    {
      id: '1',
      name: 'Standard Business Hours',
      type: 'business',
      hours: {
        monday: { open: '09:00', close: '21:00' },
        tuesday: { open: '09:00', close: '21:00' },
        wednesday: { open: '09:00', close: '21:00' },
        thursday: { open: '09:00', close: '21:00' },
        friday: { open: '09:00', close: '22:00' },
        saturday: { open: '09:00', close: '22:00' },
        sunday: { open: '10:00', close: '20:00' },
      },
      createdAt: new Date('2024-01-01'),
      lastUsed: new Date('2024-01-15'),
    },
    {
      id: '2',
      name: 'Holiday Hours',
      type: 'holiday',
      hours: {
        monday: { open: '10:00', close: '18:00' },
        tuesday: { open: '10:00', close: '18:00' },
        wednesday: { open: '10:00', close: '18:00' },
        thursday: { open: '10:00', close: '18:00' },
        friday: { open: '10:00', close: '18:00' },
        saturday: { open: '10:00', close: '18:00' },
        sunday: { open: '12:00', close: '17:00' },
      },
      createdAt: new Date('2024-01-10'),
    },
    {
      id: '3',
      name: 'Weekend Extended',
      type: 'weekend',
      hours: {
        monday: { open: '09:00', close: '21:00' },
        tuesday: { open: '09:00', close: '21:00' },
        wednesday: { open: '09:00', close: '21:00' },
        thursday: { open: '09:00', close: '22:00' },
        friday: { open: '09:00', close: '23:00' },
        saturday: { open: '09:00', close: '23:00' },
        sunday: { open: '10:00', close: '22:00' },
      },
      createdAt: new Date('2024-01-05'),
    },
  ]);

  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState('');

  const getTemplateIcon = (type: StoreHoursTemplate['type']) => {
    switch (type) {
      case 'holiday':
        return <HolidayIcon fontSize="small" />;
      case 'weekend':
        return <WeekendIcon fontSize="small" />;
      case 'business':
        return <BusinessIcon fontSize="small" />;
      default:
        return <TemplateIcon fontSize="small" />;
    }
  };

  const getTemplateColor = (type: StoreHoursTemplate['type']) => {
    switch (type) {
      case 'holiday':
        return 'warning';
      case 'weekend':
        return 'info';
      case 'business':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleApply = () => {
    if (selectedTemplate && onApplyTemplate) {
      onApplyTemplate(selectedTemplate);
      onClose();
    }
  };

  const handleSave = () => {
    // TODO: Implement template save via API
    onClose();
  };

  const renderContent = () => {
    switch (mode) {
      case 'save':
        return (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Save the current hours from selected store as a template for future use.
            </Alert>
            <Stack spacing={2}>
              <TextField
                label="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                fullWidth
                placeholder="e.g., Summer Hours, Black Friday Hours"
                required
              />
              <FormControl fullWidth>
                <InputLabel>Template Type</InputLabel>
                <Select defaultValue="custom" label="Template Type">
                  <MenuItem value="custom">Custom</MenuItem>
                  <MenuItem value="business">Business Hours</MenuItem>
                  <MenuItem value="holiday">Holiday Hours</MenuItem>
                  <MenuItem value="weekend">Weekend Hours</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Hours from: Store {selectedStoreId || Array.from(selectedStores)[0]?.storeNumber}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Mon-Fri: 9:00 AM - 10:00 PM
                  <br />
                  Sat-Sun: 10:00 AM - 9:00 PM
                </Typography>
              </Box>
            </Stack>
          </>
        );

      case 'apply':
        return (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Select a template to apply to {selectedStores.size} selected store(s).
            </Alert>
            <List>
              {templates.map((template) => (
                <React.Fragment key={template.id}>
                  <ListItemButton
                    selected={selectedTemplate === template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                      {getTemplateIcon(template.type)}
                    </Box>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">{template.name}</Typography>
                          <Chip
                            label={template.type}
                            size="small"
                            color={getTemplateColor(template.type)}
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          M-F: {template.hours.monday.open} - {template.hours.monday.close} |
                          Sat: {template.hours.saturday.open} - {template.hours.saturday.close} |
                          Sun: {template.hours.sunday.open} - {template.hours.sunday.close}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
            </List>
          </>
        );

      default:
        return (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Manage your store hours templates.
            </Alert>
            <List>
              {templates.map((template) => (
                <ListItem key={template.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                    {getTemplateIcon(template.type)}
                  </Box>
                  <ListItemText
                    primary={template.name}
                    secondary={`Created: ${template.createdAt.toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" aria-label="delete">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </>
        );
    }
  };

  const getDialogTitle = () => {
    switch (mode) {
      case 'save':
        return 'Save Hours Template';
      case 'apply':
        return 'Apply Hours Template';
      default:
        return 'Manage Hours Templates';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          {getDialogTitle()}
        </Box>
      </DialogTitle>
      <DialogContent>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {mode === 'save' && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!templateName}
          >
            Save Template
          </Button>
        )}
        {mode === 'apply' && (
          <Button
            variant="contained"
            startIcon={<ScheduleIcon />}
            onClick={handleApply}
            disabled={!selectedTemplate}
          >
            Apply to {selectedStores.size} Store(s)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};